import './ui.css';
import type { ComponentType } from 'preact';
import { computed, effect } from '@preact/signals';
import { useEffect, useMemo } from 'preact/hooks';
import { installRoomWatch } from '@hf/client-core/ui-model/room-watch';
import { ActionsContext, ServicesContext, type UiActions, type UiServices } from './actions';
import { ensureManifest } from './assets';
import { ConsentPrompt } from './overlays/ConsentPrompt';
import { SignInGate } from './overlays/SignInGate';
import { AccountDialogs } from './overlays/AccountDialogs';
import { requireWebSignIn } from './wallet';
import { MANIFEST_URL, PORTAL } from '../portal';
import { Hud } from './hud/Hud';
import { StatsOverlay } from './hud/StatsOverlay';
import { SuperTitleLayer } from './hud/SuperTitleLayer';
import { Toasts } from './hud/Toasts';
import { createMomentTracker } from '@hf/client-core/ui-model/moments';
import { installMenuRouter } from './nav';
import { NetBanner } from './overlays/NetBanner';
import { PauseOverlay } from './overlays/Pause';
import { CharacterSelect } from './screens/CharacterSelect';
import { MainMenu } from './screens/MainMenu';
import { ModeSelect } from './screens/ModeSelect';
import { OnlineLobby } from './screens/OnlineLobby';
import { PracticeSetup } from './screens/PracticeSetup';
import { Profile } from './screens/Profile';
import { Results } from './screens/Results';
import { Settings } from './screens/Settings';
import { Store } from './screens/Store';
import { Title } from './screens/Title';
import { TutorialHub } from './screens/TutorialHub';
import { Versus } from './screens/Versus';
import { applySavedBindings } from '@hf/client-core/ui-model/settings-apply';
import { HowToPlayHost } from './tutorial/HowToPlay';
import { newHereEligible } from '@hf/client-core/ui-model/tutorial/progress';
import { ui } from '@hf/client-core/ui-model/store';
import type { ScreenId } from '@hf/client-core/ui-model/types';

const SCREENS: Partial<Record<ScreenId, ComponentType>> = {
  title: Title,
  main_menu: MainMenu,
  mode_select: ModeSelect,
  character_select: CharacterSelect,
  versus: Versus,
  match: Hud,
  results: Results,
  online_lobby: OnlineLobby,
  settings: Settings,
  profile: Profile,
  tutorial: TutorialHub,
  practice: PracticeSetup,
  store: Store,
};

/** Marks the main menu reached from the Title (Football 101's first-launch offer appears there only). */
const trackTitleToMenu = (): (() => void) => {
  let prev = ui.screen.peek();
  return effect(() => {
    const s = ui.screen.value;
    if (s !== prev) {
      newHereEligible.value = prev === 'title' && s === 'main_menu';
      prev = s;
    }
  });
};

/** Only changes when the saved rebinding overrides change (not on every volume tweak). */
const savedBindings = computed(() => ui.settings.value.bindings);

/** Root of the DOM overlay. Routes on `ui.screen`; installs menu routing, art, broadcast moments and settings. */
export function App({ actions: raw, services }: { actions: UiActions; services: UiServices }) {
  // Online (lobby + leaderboard) and the store need an account: without one the sign-in gate comes back instead.
  const actions = useMemo<UiActions>(
    () => ({
      ...raw,
      goto: (s) => {
        if (PORTAL && (s === 'online_lobby' || s === 'store' || s === 'profile')) return;
        if ((s === 'online_lobby' || s === 'store') && !requireWebSignIn()) return;
        raw.goto(s);
      },
    }),
    [raw],
  );
  useEffect(() => {
    void ensureManifest(MANIFEST_URL);
    const disposers = [
      installMenuRouter(services.input),
      createMomentTracker(ui),
      // Settings → services (boot applies volumes once; keep them live, plus touch mode and rebinding).
      effect(() => {
        const s = ui.settings.value;
        try {
          services.audio.setVolumes(s.volumes);
        } catch {
          /* audio optional */
        }
        services.input.setTouchMode(s.touchControls);
      }),
      // Rebinding persistence: re-apply whenever the saved overrides change (the Settings screen writes them).
      effect(() => applySavedBindings(services.input, savedBindings.value)),
      // Football 101's first-launch offer follows the Title → main menu step only.
      trackTitleToMenu(),
      // Private rooms: the host's rules and "X LEFT THE ROOM" survive the lobby being unmounted (Results, match).
      installRoomWatch(services.online, ui),
    ];
    return () => {
      for (const d of disposers) d();
    };
  }, [services]);

  const screen = ui.screen.value;
  const s = ui.settings.value;
  const Screen = SCREENS[screen];
  const inMatch = screen === 'match';
  return (
    <ServicesContext.Provider value={services}>
      <ActionsContext.Provider value={actions}>
        <div
          class={`hf-root ${s.reducedMotion ? 'reduced-motion' : ''} ${inMatch ? 'in-match' : 'in-menu'}`}
          data-screen={screen}
        >
          {Screen ? <Screen key={screen} /> : null}
          <SuperTitleLayer />
          {inMatch ? <PauseOverlay /> : null}
          <HowToPlayHost />
          {PORTAL ? null : (
            <>
              <NetBanner />
              <ConsentPrompt />
              <SignInGate />
              <AccountDialogs />
            </>
          )}
          <Toasts />
          <StatsOverlay />
          {s.crt && !inMatch && screen !== 'title' ? <div class="hf-crt" aria-hidden="true" /> : null}
          <div class="hf-rotate" role="alert">
            ROTATE YOUR PHONE
            <span>Hand Egg plays in landscape.</span>
          </div>
        </div>
      </ActionsContext.Provider>
    </ServicesContext.Provider>
  );
}
